// Modo de seleção das listas (REGRAS 1.4): segurar ~3 s parado (ou ⋮ › Selecionar) marca vários itens.
// No topo: quantos estão selecionados, "Selecionar todos" e Cancelar. Embaixo, numa faixa fixa: Arquivar, Duplicar,
// Mover para…, Enviar e Excluir (só as que fazem sentido ali). Itens grandes não se excluem em grupo: só um por um.
// Texto 29 (item 2) — CAUSA de "seleciona e não aparece nenhuma ação": a barra antiga (sem "genero") desenhava só
// Arquivar e Excluir e ignorava as ações a mais; nas caixas de Anotações as ações (Mover para…, Excluir) eram "a mais".
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { mostrarFaixa, type AcaoFaixa } from '../lib/faixaSelecao';
import { Alert, Pressable, Text, View } from 'react-native';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

export function useSelecao<T>({ itens, chave, cor, grande, onArquivar, onExcluir, onDuplicar, genero, extras }: {
  itens: T[]; chave: (x: T) => string; cor: string;
  grande?: (x: T) => boolean;                  // item grande: não exclui em grupo
  onArquivar?: (ids: string[]) => void;
  onExcluir?: (ids: string[]) => void;         // só para itens simples
  onDuplicar?: (ids: string[]) => void;        // texto 29 (REGRAS 1.4)
  /** 'f' = matérias, páginas, sessões; 'm' = fichários (só muda o "selecionada(s)" / "selecionado(s)"). */
  genero?: 'f' | 'm';
  /** ações a mais (ex.: Mover para…, Enviar para o My Planner) */
  extras?: { label: string; onPress: (ids: string[]) => void }[];
}) {
  const [marcados, setMarcados] = useState<Set<string> | null>(null);
  const dono = useRef('sel' + Math.random().toString(36).slice(2)).current;
  const ativa = !!marcados;
  const cancelar = () => setMarcados(null);
  const sel = itens.filter(x => marcados?.has(chave(x)));
  const temGrande = !!grande && sel.some(grande);
  const prop = {
    ativa, marcados: marcados ?? new Set<string>(), cor,
    onIniciar: (k: string) => setMarcados(new Set([k])),
    onAlternar: (k: string) => setMarcados(m => { const n = new Set(m ?? []); if (n.has(k)) n.delete(k); else n.add(k); return n.size ? n : null; }),
    onCancelar: cancelar,
  };
  const ids = sel.map(chave);
  const excluir = () => {
    if (!onExcluir || !ids.length) return;
    if (temGrande) return Alert.alert(t('Não dá para excluir em grupo'), t('Há itens grandes na seleção. Eles só podem ser excluídos um por um, com confirmação e senha.'));
    Alert.alert(ids.length === 1 ? t('Excluir 1 item?') : t('Excluir {n} itens?', { n: ids.length }), t('Ficam 30 dias na lixeira.'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Excluir'), style: 'destructive', onPress: () => { onExcluir(ids); cancelar(); } }]);
  };
  const fem = genero === 'f';
  const rotuloN = ids.length === 1 ? (fem ? t('{n} selecionada', { n: 1 }) : t('{n} selecionado', { n: 1 }))
    : (fem ? t('{n} selecionadas', { n: ids.length }) : t('{n} selecionados', { n: ids.length }));
  // texto 29 (item 2, REGRAS 1.4): as ações ficam numa faixa FIXA embaixo da tela (lib/faixaSelecao)
  const acoes: AcaoFaixa[] = [
    ...(onArquivar ? [{ label: '📦 ' + t('Arquivar'), cor: C.warn, onPress: () => { if (ids.length) { onArquivar(ids); cancelar(); } } }] : []),
    ...(onDuplicar ? [{ label: '⧉ ' + t('Duplicar'), onPress: () => { if (ids.length) { onDuplicar(ids); cancelar(); } } }] : []),
    ...(extras ?? []).map(x => ({ label: x.label, onPress: () => { if (ids.length) { x.onPress(ids); cancelar(); } } })),
    ...(onExcluir ? [{ label: '🗑 ' + t('Excluir'), cor: temGrande ? C.mute : C.danger, desativada: temGrande, onPress: excluir }] : []),
  ];
  const chaveAcoes = acoes.map(a => a.label).join('|') + ':' + ids.join(',');
  const refAcoes = useRef(acoes); refAcoes.current = acoes;
  useEffect(() => {
    if (!ativa) { mostrarFaixa(dono, null); return; }
    mostrarFaixa(dono, { cor, acoes: refAcoes.current, aviso: temGrande ? t('Itens grandes só se excluem um por um.') : undefined });
  }, [ativa, chaveAcoes, temGrande]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => mostrarFaixa(dono, null), [dono]);
  // no topo: o contador, "Selecionar todos" e Cancelar
  const barra: ReactNode = ativa ? (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 10, borderRadius: 14, backgroundColor: alpha(cor, 0.15), borderWidth: 1, borderColor: alpha(cor, 0.4) }}>
      <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>{rotuloN}</Text>
      <Pressable onPress={() => setMarcados(new Set(itens.map(chave)))} hitSlop={6}><Text style={{ color: cor, fontWeight: '700', fontSize: 13 }}>{fem ? t('Selecionar todas') : t('Selecionar todos')}</Text></Pressable>
      <Pressable onPress={cancelar} hitSlop={6}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Cancelar')}</Text></Pressable>
    </View>
  ) : null;
  /** ⋮ › Selecionar (REGRAS 1.9): entra na seleção com este item marcado */
  const selecionar = (k: string) => setMarcados(m => { const n = new Set(m ?? []); n.add(k); return n; });
  return { prop, barra, ativa, cancelar, selecionar, aviso: temGrande ? t('Itens grandes só se excluem um por um.') : '' };
}

/** Padrão único dos arquivados (0.65, o mesmo do Diário): no fim da lista, uma linha recolhida "📦 Arquivados (N) ▸";
 *  um toque abre ali mesmo, na página, os itens arquivados, cada um com "Restaurar" e "Excluir".
 *  qualificador: só quando há duas listas de arquivados na mesma tela (ex.: Casa, tarefas e cômodos) → "📦 Arquivados · Cômodos (N)".
 *  onExcluir: quem chama confirma ("Fica 30 dias na lixeira.") e manda para a lixeira, como no resto da tela. */
export function Arquivados<T>({ itens, chave, render, onRestaurar, onExcluir, podeExcluir, cor, qualificador }: {
  itens: T[]; chave: (x: T) => string; render: (x: T) => ReactNode; cor: string; qualificador?: string;
  onRestaurar: (x: T) => void; onExcluir?: (x: T) => void;
  podeExcluir?: (x: T) => boolean;   // esconde o Excluir de um item que a pessoa não pode apagar (ex.: documento de outra pessoa)
}) {
  const [aberto, setAberto] = useState(false);
  if (!itens.length) return null;
  return (
    <View style={{ gap: 6, marginTop: 8 }}>
      <Pressable onPress={() => setAberto(a => !a)} hitSlop={6} accessibilityRole="button" accessibilityState={{ expanded: aberto }}>
        <Text style={{ color: C.sub, fontSize: 13 }}>{aberto ? '▾' : '▸'} 📦 {t('Arquivados')}{qualificador ? ' · ' + qualificador : ''} ({itens.length})</Text>
      </Pressable>
      {aberto && itens.map(x => (
        <View key={chave(x)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 14, padding: 12, opacity: 0.85 }}>
          <View style={{ flex: 1, minWidth: 0 }}>{render(x)}</View>
          <Pressable onPress={() => onRestaurar(x)} hitSlop={4} accessibilityLabel={t('Restaurar')}
            style={{ paddingVertical: 6, paddingHorizontal: 10, borderRadius: 18, backgroundColor: cor, borderWidth: 1, borderColor: cor }}>
            <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>↩︎ {t('Restaurar')}</Text>
          </Pressable>
          {!!onExcluir && (!podeExcluir || podeExcluir(x)) && (
            <Pressable onPress={() => onExcluir(x)} hitSlop={4} accessibilityLabel={t('Excluir')}
              style={{ paddingVertical: 6, paddingHorizontal: 10, borderRadius: 18, backgroundColor: alpha(C.danger, 0.12), borderWidth: 1, borderColor: alpha(C.danger, 0.5) }}>
              <Text style={{ color: C.danger, fontSize: 12, fontWeight: '600' }}>🗑 {t('Excluir')}</Text>
            </Pressable>
          )}
        </View>
      ))}
    </View>
  );
}
